import * as _ from 'lodash';
import { refreshAccessToken } from 'api/utils/refreshTokenUtils';
import { LOGIN } from 'constants/routes';
import { useAuthStore } from 'stores/authStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { toastr } from 'utils';
import history from 'utils/history';
import env from '../config';
import AxiosProvider from './AxiosProvider';

class CoreApiRambla {
  constructor(baseUrl = env.apiBaseUrlRambla) {
    this.api = AxiosProvider(baseUrl);
    this.api.defaults.withCredentials = true;
    this.baseUrl = baseUrl;

    this.afterResponse = this.afterResponse.bind(this);
    this.beforeRequest = this.beforeRequest.bind(this);
    this.responseError = this.responseError.bind(this);
    this.setInterceptors(
      this.beforeRequest,
      this.requestError,
      this.afterResponse,
      this.responseError,
    );
  }

  setInterceptors(beforeRequest, requestError, afterResponse, responseError) {
    this.api.interceptors.request.use(beforeRequest, requestError);
    this.api.interceptors.response.use(afterResponse, responseError);
  }

  setEndpointUrl(url) {
    this.api.defaults.baseURL = `${this.baseUrl}/${url}`;
  }

  beforeRequest(config) {
    if ('requireAuth' in config && config.requireAuth === false) {
      return config;
    }

    const { accessToken } = useAuthStore.getState();
    const { selectedBrandUid, selectedRegionUid } = useSelectedProfileStore.getState();

    if (accessToken) {
      const originalRequest = config;
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;

      if (selectedBrandUid && selectedRegionUid) {
        originalRequest.headers.Brand = selectedBrandUid;
        originalRequest.headers.Region = selectedRegionUid;
      }

      return originalRequest;
    }

    return new Error('User not logged in');
  }

  requestError(error) {
    throw error;
  }

  afterResponse(resp) {
    return resp.data;
  }

  async responseError(error) {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return this.api(originalRequest);
      } catch {
        useAuthStore.getState().clearAuth();
        if (history.location.pathname !== LOGIN) {
          history.push(LOGIN);
        }
        return Promise.reject(error);
      }
    }

    const notifyErrorsFlag = error.response && error.response.config.notifyErrors;
    const showNotifyErrors = notifyErrorsFlag === undefined || notifyErrorsFlag === true;

    if (error.response) {
      if (status === 500) {
        console.error('Server Error (500)');
      } else if (status === 404) {
        if (showNotifyErrors) {
          toastr.error('Not Found (404)', 'The requested resource was not found.');
        }
      } else if (showNotifyErrors) {
        if (_.isArray(error.response)) {
          toastr.error('Error occured', error.response.join('\n'));
        } else {
          error.response.data && _.isObject(error.response.data)
            ? toastr.error('Error occurred', parseErrorResponse(error.response.data))
            : toastr.error('Error occurred', 'Unexpected error, please try again.');
        }
      }
    } else {
      throw error;
    }

    const parseErrorResponse = (err) => {
      let errorPlain = '';
      Object.keys(err).forEach((key) => {
        const errResponse = err[key].reduce((acc, val) => `${acc} ${val} `, '');
        errorPlain += `${key.toUpperCase()}: ${errResponse}}`;
      });
      return errorPlain;
    };

    return Promise.reject(error);
  }
}

export default CoreApiRambla;
